import "./LiveClock.css";
import { useEffect, useRef, useState } from "react";
import { onTimeFreeze } from "../../utils/timeFreeze";

const CATCH_UP_MS = 700; // how long the clock takes to race back to the real time
const pad = (n, size = 2) => String(n).padStart(size, "0");

// Milliseconds until the next second starts
const untilNextSecond = (date) => 1000 - date.getMilliseconds();

/*
 * The visitor's local time as HH:MM:SS (24-hour). It updates exactly when the
 * second changes, so it never lags behind the device clock.
 *
 * "Freeze time" (holding a gallery photo): the clock glitches for a moment,
 * reveals its milliseconds and stops on them. When time is released it races
 * forward to catch up with the real time, then the milliseconds tuck away.
 */
function LiveClock({ className = "" }) {
  const [now, setNow] = useState(() => new Date());
  // What the clock shows while frozen or catching up (null = normal)
  const [shown, setShown] = useState(null);
  const [mode, setMode] = useState("normal"); // normal | frozen | catching-up
  const [glitch, setGlitch] = useState(0); // bumps to replay the glitch
  const frameRef = useRef(null);

  useEffect(() => {
    let timer;
    const tick = () => {
      const date = new Date();
      setNow(date);
      timer = setTimeout(tick, untilNextSecond(date));
    };
    timer = setTimeout(tick, untilNextSecond(new Date()));
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    let frozenAt = null;
    let hideTimer = null;

    const stop = onTimeFreeze((frozen) => {
      cancelAnimationFrame(frameRef.current);
      clearTimeout(hideTimer);

      if (frozen) {
        frozenAt = new Date();
        setShown(frozenAt);
        setMode("frozen");
        setGlitch((n) => n + 1);
        return;
      }
      if (!frozenAt) return;

      // Catch up: run the clock fast from the frozen moment to now
      const from = frozenAt.getTime();
      const startedAt = performance.now();
      frozenAt = null;
      setMode("catching-up");
      const step = (time) => {
        const t = Math.min((time - startedAt) / CATCH_UP_MS, 1);
        const eased = 1 - (1 - t) ** 3;
        setShown(new Date(from + (Date.now() - from) * eased));
        if (t < 1) {
          frameRef.current = requestAnimationFrame(step);
        } else {
          setNow(new Date());
          // Let the final time sit for a beat, then tuck the milliseconds away
          hideTimer = setTimeout(() => {
            setMode("normal");
            setShown(null);
          }, 500);
        }
      };
      frameRef.current = requestAnimationFrame(step);
    });

    return () => {
      stop();
      cancelAnimationFrame(frameRef.current);
      clearTimeout(hideTimer);
    };
  }, []);

  const time = shown || now;
  const withMilliseconds = mode !== "normal";

  return (
    <time
      className={`${className} live-clock is-${mode}`}
      dateTime={time.toISOString()}
      aria-label={mode === "frozen" ? "Time frozen" : "Your local time"}
    >
      {/* key replays the glitch animation each time time freezes */}
      <span className={glitch && mode === "frozen" ? "live-clock-glitch" : ""} key={glitch}>
        {pad(time.getHours())}:{pad(time.getMinutes())}:{pad(time.getSeconds())}
        <span className={`live-clock-ms ${withMilliseconds ? "is-shown" : ""}`}>
          <span>.{pad(time.getMilliseconds(), 3)}</span>
        </span>
      </span>
    </time>
  );
}

export default LiveClock;
